/**
 * ConversationalChat.jsx — Earn-E AI Conversational Chat Page
 * URL: /ai-chat (or /chat)
 *
 * Free-form conversational chat powered by GPT-4o-mini. Unlike the structured
 * OnboardingChat questionnaire, this feels like talking to a knowledgeable
 * friend. Extracts profile data silently from natural conversation.
 *
 * Supports the "Try All 3 → Get 3 Months Free" promo via isDoingPromo prop.
 * Now uses direct fetch() instead of httpsCallable()
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  CircularProgress,
  Chip,
  Fade,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSearchParams } from 'react-router-dom';

// ─── Constants ──────────────────────────────────────────────────────────────
const SIGNUP_URL = 'https://earn-echore.app/auth';
const FUNCTION_URL = 'https://us-central1-family-hub-9ae4e.cloudfunctions.net/conversationalChat';

function generateSignupCode() {
  return 'EC-' + Math.random().toString(36).substring(2, 8).toUpperCase();
}

// ─── Message Components ──────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isBot = msg.role === 'bot';
  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        flexDirection: isBot ? 'row' : 'row-reverse',
        gap: 1,
        py: 0.5,
        px: 0,
      }}
    >
      <ListItemAvatar sx={{ minWidth: 40 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: isBot ? 'primary.main' : 'secondary.main',
            fontSize: 18,
          }}
        >
          {isBot ? '🤖' : <PersonIcon fontSize="small" />}
        </Avatar>
      </ListItemAvatar>
      <Box
        sx={{
          maxWidth: '75%',
          bgcolor: isBot ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.06)',
          borderRadius: isBot ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
          p: '10px 14px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
          {isBot ? 'Earn-E' : 'You'}
        </Typography>
        <Typography variant="body2">{msg.content}</Typography>
      </Box>
    </ListItem>
  );
}

// ─── Offer Card ─────────────────────────────────────────────────────────────
function OfferCard({ profile, isDoingPromo, signupCode }) {
  const trialDays = isDoingPromo ? 90 : 30;

  return (
    <Fade in>
      <Paper
        elevation={4}
        sx={{
          p: 3,
          mt: 2,
          mx: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          🎉 Your Custom Earn-E Plan
        </Typography>

        {profile.num_kids && (
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
            👨‍👩‍👧 {profile.num_kids} kid{profile.num_kids > 1 ? 's' : ''}
            {profile.kid_ages?.length ? ` (ages ${profile.kid_ages.join(', ')})` : ''}
          </Typography>
        )}

        {profile.challenges?.length > 0 && (
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
            🎯 Focus: {profile.challenges.slice(0, 2).join(', ')}
          </Typography>
        )}

        {profile.allowance_budget && (
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1.5 }}>
            💰 Allowance budget: ${profile.allowance_budget}/week
          </Typography>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.3)', mb: 1.5 }} />

        <Typography variant="body2" sx={{ mb: 0.5 }}>✅ Pre-populated account (kids, chores, allowance all set)</Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>✅ <strong>{trialDays} days FREE</strong> trial</Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>✅ Custom chore charts for each kid</Typography>

        {signupCode && (
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, p: 1.5, mb: 2, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Your signup code</Typography>
            <Typography variant="h6" fontWeight="bold" letterSpacing={2}>{signupCode}</Typography>
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          size="large"
          href={SIGNUP_URL}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            fontWeight: 'bold',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          Start Free Trial →
        </Button>

        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7, textAlign: 'center' }}>
          No credit card required for trial
        </Typography>
      </Paper>
    </Fade>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ConversationalChat({ isDoingPromo: propIsDoingPromo }) {
  const [searchParams] = useSearchParams();
  // Accept promo from prop OR ?promo=true URL param
  const isDoingPromo = propIsDoingPromo ?? searchParams.get('promo') === 'true';

  const initialGreeting = isDoingPromo
    ? "Hey! I'm Earn-E 🤖 I see you're doing the 'Try All 3 → Get 3 Months Free' promo — awesome! 🎉 Let's make this count. Tell me about your family — how many kids do you have and how old are they?"
    : "Hey! I'm Earn-E 🤖 I help families build chore systems that actually work. Tell me a bit about your family — how many kids do you have?";

  const [messages, setMessages] = useState([
    { role: 'bot', content: initialGreeting },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const [complete, setComplete] = useState(false);
  const [signupCode, setSignupCode] = useState('');
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = useCallback(async () => {
    const userMessage = input.trim();
    if (!userMessage || loading) return;

    setInput('');
    setError('');
    setLoading(true);

    // Optimistically add user message
    const updatedMessages = [
      ...messages,
      { role: 'user', content: userMessage },
    ];
    setMessages(updatedMessages);

    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory: messages, // send history before user's new message
          profile,
          isDoingPromo,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const { response: botResponse, extractedData, isComplete } = result;

      setMessages(prev => [...prev, { role: 'bot', content: botResponse }]);

      const mergedProfile = { ...profile, ...extractedData };
      setProfile(mergedProfile);

      if (isComplete && !complete) {
        setComplete(true);
        const code = generateSignupCode();
        setSignupCode(code);
        // Append offer message after a short delay
        setTimeout(() => {
          setMessages(prev => [
            ...prev,
            {
              role: 'bot',
              content: isDoingPromo
                ? "I've got everything I need! 🎉 Since you're doing the promo, you get 90 days FREE. Check out your custom plan below!"
                : "I've got everything I need! Here's your custom plan — ready to get started?",
            },
          ]);
        }, 800);
      }

    } catch (err) {
      console.error('Chat error:', err);
      const errorMessage = err.message || 'Unknown error';
      setError(`Chat error: ${errorMessage}. Check console (F12) for details.`);
      setMessages(prev => [
        ...prev,
        { role: 'bot', content: `Oops, something went wrong: ${errorMessage}. Check browser console (F12) and email support@earn-echore.app if this continues. 😅` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, profile, isDoingPromo, complete]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40, fontSize: 22 }}>
          🤖
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold">Chat with Earn-E</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {isDoingPromo ? '🎁 Promo: 90 Days Free' : 'AI Family Assistant'}
          </Typography>
        </Box>
        {isDoingPromo && (
          <Chip
            label="PROMO ACTIVE"
            size="small"
            sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
          />
        )}
      </Box>

      {/* Chat Area */}
      <Box sx={{ flex: 1, maxWidth: 800, width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', pb: 2 }}>
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            mx: { xs: 1, sm: 2 },
            mt: 2,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 400,
            maxHeight: 'calc(100vh - 280px)',
          }}
        >
          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <List disablePadding>
              {messages.map((msg, idx) => (
                <MessageBubble key={idx} msg={msg} />
              ))}
              {loading && (
                <ListItem sx={{ px: 0 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 18 }}>
                      🤖
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Earn-E is typing…</Typography>
                  </Box>
                </ListItem>
              )}
            </List>
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          {!complete && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Type your message… (Enter to send)"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={loading}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': { borderRadius: 3 },
                    '& .MuiInputBase-input': { minHeight: 48 },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  sx={{ minWidth: 48, minHeight: 48, borderRadius: 3, px: 2 }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          )}

          {complete && (
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ mb: 0.5 }} />
              <Typography variant="body2" color="success.main" fontWeight="bold">
                Conversation complete!
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Offer Card (shown when complete) */}
        {complete && (
          <OfferCard
            profile={profile}
            isDoingPromo={isDoingPromo}
            signupCode={signupCode}
          />
        )}

        {/* Footer links */}
        <Box sx={{ px: 1, mt: 2, textAlign: 'center', wordBreak: 'break-all' }}>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>
            Questions? Email{' '}
            <Link href="mailto:support@earn-echore.app" underline="hover" sx={{ wordBreak: 'break-all' }}>
              support@earn-echore.app
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}